import 'cropperjs/dist/cropper.css';
import { useEffect, useRef, useState } from 'react';
import { Cropper, ReactCropperElement } from 'react-cropper';
import { Trans, useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { ReactComponent as FileIcon } from '../../assets/svg/File.svg';
import { FSObjectCategory, useCreateUploadV1Mutation, useDeleteContentCoverMutation, useDeleteFileMutation, useLazyGetFileQuery } from '../../services/content_v1';
import { extractErrorDetail } from '../common';
import Button from './Button/Button';
import FormEntryComment from './FormEntryComment';
import FormEntryStatus from './FormEntryStatus';
import FormUploaderImage from './FormUploaderImage';
import FormUploaderZone from './FormUploaderZone';
import { Modal } from './Modal';
import { ErrorModal } from './Modals/ErrorModal';

const FormUploaderZoneHint = () => <>
    <Trans>
        Drag file here
    </Trans>
    <br />
    <span>
        <Trans>
            or select on computer
        </Trans>
    </span>
</>;

interface UnitCoverUploaderProps {
    has: boolean,
    label: string,
    accept?: string,
    url?: string,
    delete_url?: string,
    onDelete?: () => void,
    onChange?: (uid: number) => void,
    checked: boolean | null,
    setChecked?: (v: boolean | null) => void,
    comment: string | undefined,
    setComment?: (v: string | undefined) => void,
    moderateable: boolean,
    moderation?: boolean,
    account_id?: number,
    cropper?: boolean,
    cropperAspectRatio?: number,
}

function validateTrailerFile(f: File) {
    if (f?.size > 50 * 1024 * 1024) {
        // more 50Mb
        return false;
    }
    const extention = f.name.split('.').pop() || '';
    if (!['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(extention.toLocaleLowerCase())) {
        return false;
    }
    return true;
}

function UnitCoverUploaderV1({ has, label, accept, url, delete_url, onDelete, onChange, checked, setChecked, comment, setComment, moderateable, moderation, account_id, cropper, cropperAspectRatio, }: UnitCoverUploaderProps) {
    const { t } = useTranslation();
    const cropperRef = useRef<ReactCropperElement>(null);

    const [src, setSrc] = useState('');

    const [progress, setProgress] = useState<number | null>(null);
    const [filename, setFilename] = useState('');
    const [fileId, setFileId] = useState<number | null>(null);
    const [unsaved, setUnsaved] = useState('');
    const [error, setError] = useState('');
    const [errorTitle] = useState<string>(t('Cover'));
    const [getFile, { data: uploaded }] = useLazyGetFileQuery();
    const [createUpload] = useCreateUploadV1Mutation();
    const [deleteFile] = useDeleteFileMutation();
    const [deleteCover] = useDeleteContentCoverMutation();

    useEffect(() => {
        if (fileId) {
            getFile(fileId);
        }
    }, [fileId]);

    useEffect(() => {
        if (uploaded?.results.signedurl) {
            setUnsaved(uploaded?.results.signedurl);
        }
    }, [uploaded]);

    useEffect(() => {
        if (has) {
            setUnsaved('');
        }
    }, [has]);

    return <div className="form__uploader _image" style={{ paddingRight: moderation ? undefined : '0px' }}>
        {/* <Button onClick={() => attachUpload({id: 2, file_id: 187, file_type: 'cover'})}></Button> */}
        <ErrorModal isOpen={!!error} onClose={() => setError('')} content={error} title={errorTitle} />

        <Modal isOpen={!!src} onClose={() => setSrc('')}>
            <div>
                <div style={{ width: '100%' }}>
                    <br />
                    <Cropper
                        src={src}
                        style={{ height: 400, width: '100%' }}
                        // Cropper.js options
                        initialAspectRatio={cropperAspectRatio || 16 / 9}
                        // preview=".img-preview"
                        ref={cropperRef}
                        viewMode={1}
                        guides={true}
                        minCropBoxHeight={10}
                        minCropBoxWidth={10}
                        background={false}
                        responsive={false}
                        checkOrientation={false}
                        controls={true}
                        cropBoxResizable={false}
                        cropBoxMovable={true}
                        zoomable={true}
                        scalable
                    />
                </div>
                <div style={{ padding: '4px', textAlign: 'center' }}>
                    <Button size="small" onClick={() => {
                        if (typeof cropperRef.current?.cropper !== 'undefined') {
                            const cropData2 = cropperRef.current?.cropper.getCroppedCanvas().toDataURL();
                            const dataurl = cropData2;
                            const arr = dataurl.split(',');
                            const mime = arr.at(0)?.match(/:(.*?);/)?.at(1); // Extracts "image/png"
                            const b64data = arr[1]; // Extracts the base64 encoded data
                            const binary = atob(b64data);

                            const array = [];
                            for (let i = 0; i < binary.length; i++) {
                                array.push(binary.charCodeAt(i));
                            }
                            const uint8Array = new Uint8Array(array);
                            const blob = new Blob([uint8Array], { type: mime });
                            const fname = 'example.png'; // Provide a desired filename
                            const file = new File([blob], fname, { type: mime });

                            const uuid = uuidv4();

                            setProgress(0);
                            setFilename(fname);

                            createUpload({
                                uuid: uuid,
                                account_id: account_id,
                                name: file.name,
                                cat: FSObjectCategory.COVER,
                                file: file,
                                onProgress: (e) => {
                                    setProgress(Math.ceil((e.progress || 0) * 100));
                                }
                            }).unwrap().then((d) => {
                                setFileId(d.results.file_id);
                                onChange && onChange(d.results.file_id);
                                setProgress(null);
                                setSrc('');
                            }).catch((err) => {
                                setProgress(null);
                                setError(extractErrorDetail(err));
                                setSrc('');
                            });

                            // createUpload({
                            //     uuid: uuid,
                            //     account_id: account_id,
                            //     name: file.name,
                            //     cat: FSObjectCategory.COVER,
                            //     file: file,
                            //     onProgress: (e) => {
                            //         setProgress(Math.ceil((e.progress || 0) * 100));
                            //     }
                            // }).unwrap().then((d) => {
                            //     setFileId(d.results.id);
                            //     onChange && onChange(d.results.file_id);
                            //     setProgress(null);
                            //     setSrc('');
                            // }).catch((err) => {
                            //     setProgress(null);
                            //     setSrc('');
                            //     setError(extractErrorDetail(err));
                            // });
                        }
                    }}><Trans>Save</Trans></Button>
                </div>
                <br style={{ clear: 'both' }} />
            </div>
        </Modal>

        {(!has && !unsaved) &&
            <FormUploaderZone
                hint={<FormUploaderZoneHint />}
            >
                <input
                    type="file"
                    className="form__uploader-input"
                    data-test-id="coverUploaderInput"
                    accept={accept}
                    onChange={async (fileEvent) => {
                        if (fileEvent.target.files) {
                            if (!validateTrailerFile(fileEvent.target.files[0])) {
                                setError(t('modal:UPLOAD_COVER_ERROR_FORMAT'));
                                return;
                            }

                            if (cropper) {
                                const fr = new FileReader();
                                fr.addEventListener(
                                    'load',
                                    () => {
                                        // Конвертация изображения в base64-строку
                                        setSrc(fr.result as string);
                                    },
                                    false,
                                );
                                fr.readAsDataURL(fileEvent.target.files[0]);
                            } else {
                                setProgress(0);
                                setFilename(fileEvent.target.files[0].name);

                                const uuid = uuidv4();
                                const file = fileEvent.target.files[0];

                                createUpload({
                                    uuid: uuid,
                                    account_id: account_id,
                                    name: file.name,
                                    cat: FSObjectCategory.COVER,
                                    file: file,
                                    onProgress: (e) => {
                                        setProgress(Math.ceil((e.progress || 0) * 100));
                                    }
                                }).unwrap().then((d) => {
                                    setFileId(d.results.file_id);
                                    onChange && onChange(d.results.file_id);
                                    setProgress(null);
                                }).catch((err) => {
                                    setProgress(null);
                                    setError(extractErrorDetail(err));
                                });
                            }
                        }
                    }}
                />
                {label}
                <br />
                <Trans>
                    Supported file formats
                </Trans>
                {' '}
                png, jpg, jpeg, gif <Trans>and</Trans> webp
                <br />
                <Trans>limit</Trans> 50 Mb
            </FormUploaderZone>
        }
        {progress &&
            <div className="form__uploader-progress" style={{ display: 'block' }}>
                <div className="form__uploader-progress-item">
                    <div className="form__uploader-progress-item-icon">
                        <FileIcon />
                    </div>
                    <div className="form__uploader-progress-item-bar">

                        <div className="form__uploader-progress-item-bar-name">{filename}</div>
                        <div className="form__uploader-progress-item-bar-line">
                            <div className="form__uploader-progress-item-bar-line-bar">
                                <div className="form__uploader-progress-item-bar-line-bar-inner" style={{ width: `${progress}%` }}>
                                </div>
                            </div>
                            <div className="form__uploader-progress-item-bar-line-percentage">{progress}%</div>
                        </div>
                    </div>
                    <div className="form__uploader-progress-item-cancel">
                        <Trans>Cancel</Trans>
                    </div>
                </div>
            </div>}
        {has &&
            <FormUploaderImage
                url={url}
                imageClassName="w-full flex"
                deletable
                rounded={false}
                onDelete={() => {
                    const ret = confirm(t('Are you sure you want to delete this cover?'));
                    if (ret) {
                        delete_url && deleteCover(delete_url).unwrap()
                            .then(() => onChange && onChange(-1))
                            .catch(e => {
                                setError(extractErrorDetail(e));
                            });
                        onDelete && onDelete();
                        setProgress(null);
                    }
                }}
            />
        }
        {unsaved &&
            <FormUploaderImage
                url={unsaved}
                imageClassName="w-full flex"
                deletable
                rounded={false}
                onDelete={() => {
                    const ret = confirm(t('Are you sure you want to delete this cover?'));
                    ret && fileId && deleteFile(fileId).unwrap()
                        .then(() => {
                            setFileId(null);
                            setUnsaved('');
                            onChange && onChange(-1);
                        })
                        .catch(e => {
                            setError(extractErrorDetail(e));
                        });
                }}
            />
        }
        {moderation && <>
            <FormEntryStatus value={checked} setValue={(v) => setChecked && setChecked(v)} editable={moderateable} />
            <FormEntryComment value={comment} setValue={(v) => setComment && setComment(v)} editable={moderateable} />
        </>}
    </div>
}

export default UnitCoverUploaderV1;